/**
 * The document list, indexed once so a deal can find its own documents
 * without reading all of them.
 *
 * THE MEASUREMENT THIS EXISTS FOR
 *
 * Every per-deal document question — is the signed PSA attached, is an
 * offer out, is the closing packet complete — ran
 *
 *   documents.filter((doc) => attachedIds.has(doc.id) || doc.dealId === deal.id)
 *
 * which reads the whole list per deal. With the 50 documents the board
 * loads on its own that is nothing: computeDealHealth over all 351
 * deals measured 15 ms. But opening any deal dashboard and touching its
 * documents calls loadAllDocuments, and from then on `documents` is the
 * full 25,165-row library for the rest of the session. The same 351
 * health computations then measured 3,788 ms — on the main thread, on
 * every mount of the Deal Tracker, because the cache that held them
 * lived in the component and died with it. That is "it takes forever
 * navigating back to deal tracker".
 *
 * Indexed by document id and by deal id, each lookup is the handful of
 * documents that are actually that deal's. Built once per `documents`
 * identity in the app context, so every reader shares one index and a
 * realtime insert rebuilds it once rather than per consumer.
 *
 * COMPATIBLE BY DESIGN
 *
 * Every function that takes `documents` keeps taking the plain array —
 * the offer-evidence rules, the closing-packet rules, the activity
 * timeline, the manager summary, the chat route. attachedDocumentsFor
 * accepts either shape, so a caller that has the index passes it and a
 * caller that only has an array is unchanged. Order is preserved in
 * both: the result reads in the documents' own order, as the filter's
 * did, because the closing-packet rules take the FIRST match.
 */

const INDEX = Symbol('documentIndex');

export function isDocumentIndex(value) {
  return !!(value && value[INDEX] === true);
}

export function indexDocuments(documents) {
  const all = Array.isArray(documents) ? documents : [];
  const byId = new Map();
  const byDealId = new Map();
  // Original position, so a result can be put back in list order.
  const order = new Map();
  all.forEach((doc, i) => {
    if (!doc || typeof doc !== 'object') return;
    order.set(doc, i);
    if (doc.id !== undefined && doc.id !== null) byId.set(doc.id, doc);
    if (doc.dealId) {
      if (!byDealId.has(doc.dealId)) byDealId.set(doc.dealId, []);
      byDealId.get(doc.dealId).push(doc);
    }
  });
  return { [INDEX]: true, all, byId, byDealId, order };
}

/**
 * The documents attached to a deal: those it names by id, plus those
 * filed against it. The one predicate every document rule shares.
 */
export function attachedDocumentsFor(deal, documents = []) {
  const attachedIds = Array.isArray(deal?.attachedDocumentIds) ? deal.attachedDocumentIds : [];
  const dealId = deal?.id || null;

  if (!isDocumentIndex(documents)) {
    if (!Array.isArray(documents)) return [];
    const ids = new Set(attachedIds);
    return documents.filter((doc) => ids.has(doc?.id) || (dealId && doc?.dealId === dealId));
  }

  const out = new Set(dealId ? (documents.byDealId.get(dealId) || []) : []);
  for (const id of attachedIds) {
    const doc = documents.byId.get(id);
    if (doc) out.add(doc);
  }
  if (out.size <= 1) return Array.from(out);
  return Array.from(out).sort((a, b) => documents.order.get(a) - documents.order.get(b));
}

/** The raw list behind either shape, for the few readers that need all of it. */
export function allDocumentsOf(documents = []) {
  if (isDocumentIndex(documents)) return documents.all;
  return Array.isArray(documents) ? documents : [];
}
